// Filling a diary's aid status from the Aid Planner: the VALS Diary, and LACW's week on the
// billing sheet (src/lib/billingAidStatus.js), which used to be the LACW Aid Status Diary.
//
// Each week staff read every listed matter's legal aid status and aid number off ATLAS and
// type them into the diary. The Aid Planner already knows both for any matter whose letters
// have been uploaded, so a diary row is matched to its Aid Planner matter and the status is
// filled in for staff to check and confirm, rather than to look up and type.
//
// Matching, strongest first:
//   - the diary row has an aid number and a matter has the same one
//   - the client's name matches one matter, in any order: the diary writes "JONES, Marie"
//     or "Marie JONES", the letters "Marie Jones". "Kaitlyn/Katelyn HATTON" matches either.
// A name that matches more than one matter is not guessed at; the row says so instead.
//
// The status comes from the matter's own reminders, so the diary and the Aid Planner agree:
// an extension reminder makes it "Extension required", a grant in place "Aid confirmed"
// with its aid number, an application in "Aid pending".

import { plannerMatterReminders } from "./grantReminders.js";

function words(value = "") {
  return String(value)
    .toLowerCase()
    .replace(/^\*+/, "")
    .replace(/[^a-z/\s'-]+/g, " ")
    .replace(/['-]/g, "")
    .split(/\s+/)
    .filter(Boolean);
}

// Every way of writing one person's name as a key: its words, sorted, so order and the
// "SURNAME, First" comma do not matter. A word with alternatives ("kaitlyn/katelyn") gives
// one key per alternative.
export function nameKeys(name = "") {
  const text = String(name).includes(",") ? String(name).split(",").reverse().join(" ") : String(name);
  let variants = [[]];
  for (const word of words(text)) {
    const options = word.split("/").filter(Boolean);
    variants = variants.flatMap((variant) => options.map((option) => [...variant, option]));
  }
  return new Set(variants.filter((variant) => variant.length >= 2).map((variant) => [...variant].sort().join(" ")));
}

function matterName(matter) {
  return matter?.client?.fullName
    || [matter?.client?.firstName, matter?.client?.lastName].filter(Boolean).join(" ");
}

const aidKey = (value = "") => String(value).toUpperCase().replace(/[^0-9A-Z]/g, "");

// The Aid Planner matter a diary row is about, if exactly one fits.
export function matchDiaryEntry(entry, matters = []) {
  const live = matters.filter((matter) => matter?.matterId && !matter.finalisation?.grantClosed);

  const entryAid = aidKey(entry?.aidNumber);
  if (entryAid) {
    const byAid = live.filter((matter) => aidKey(matter.aid?.aidNumber) === entryAid);
    if (byAid.length === 1) return { matter: byAid[0], by: "aid number" };
  }

  const keys = nameKeys(entry?.clientName);
  if (keys.size === 0) return null;
  const byName = live.filter((matter) => [...nameKeys(matterName(matter))].some((key) => keys.has(key)));
  if (byName.length === 1) return { matter: byName[0], by: "name" };
  if (byName.length > 1) return { ambiguous: byName.length };
  return null;
}

// What the Aid Planner says a matter's diary status is.
export function statusFromMatter(matter, options = {}) {
  const reminders = plannerMatterReminders(matter, options);
  const aidNumber = matter.aid?.aidNumber || "";
  const extension = reminders.find((reminder) => reminder.kind === "extension");
  if (extension) {
    return { status: "Extension required", aidNumber, reason: extension.reason };
  }
  const applicationStatus = String(matter.aid?.applicationStatus || "");
  if (matter.funding?.grantInPlace || /granted|approved/i.test(applicationStatus)) {
    const last = reminders.find((reminder) => reminder.kind === "extension_check");
    return { status: "Aid confirmed", aidNumber, reason: last ? last.reason : "A grant is in place." };
  }
  if (reminders.some((reminder) => reminder.kind === "new_grant")) {
    return { status: "Aid not applied for", aidNumber: "", reason: "No grant is in place for this matter." };
  }
  if (matter.application?.lodgedInAtlas || matter.funding?.aidApplied || /pending|submitted|requisition/i.test(applicationStatus)) {
    return { status: "Aid pending", aidNumber: "", reason: "An application is in and no decision is recorded." };
  }
  return null;
}

// A suggestion for every diary row that has one, by row id.
export function diarySuggestions(entries = [], matters = [], options = {}) {
  const suggestions = new Map();
  for (const entry of entries) {
    if (!entry?.id || !String(entry.clientName || "").trim()) continue;
    const match = matchDiaryEntry(entry, matters);
    if (!match) continue;
    if (match.ambiguous) {
      suggestions.set(entry.id, { ambiguous: match.ambiguous });
      continue;
    }
    const status = statusFromMatter(match.matter, options);
    if (!status) continue;
    suggestions.set(entry.id, {
      ...status,
      matterId: match.matter.matterId,
      matterName: matterName(match.matter),
      by: match.by,
    });
  }
  return suggestions;
}

// The diary rows with suggestions filled in. Only rows nobody has looked at yet ("Not
// checked", unconfirmed) are filled; a status staff chose is never overwritten.
export function fillDiaryFromAidPlanner(entries = [], suggestions = new Map(), now = new Date()) {
  let changed = false;
  const next = entries.map((entry) => {
    const suggestion = suggestions.get(entry.id);
    if (!suggestion?.status || entry.confirmed || entry.aidStatus !== "Not checked") return entry;
    changed = true;
    return {
      ...entry,
      aidStatus: suggestion.status,
      aidNumber: suggestion.status === "Aid confirmed" ? suggestion.aidNumber || entry.aidNumber : entry.aidNumber,
      aidPlannerFill: { matterId: suggestion.matterId, status: suggestion.status, reason: suggestion.reason || "", at: now.toISOString() },
    };
  });
  return changed ? next : entries;
}

// The VALS Diary names one status differently ("Aid in place" for "Aid confirmed"), and has
// no "Not checked": an uploaded week starts every row at "In house", which is also a real
// status. So an uploaded row is marked aidStatusFromImport, and only rows still carrying that
// mark (and not confirmed by the solicitor) are filled. Rows from weeks saved before the mark
// existed are never filled; they get the "Aid Planner says … Use this" hint instead.
const VALS_STATUS_NAMES = { "Aid confirmed": "Aid in place" };

export function valsDiarySuggestions(entries = [], matters = [], options = {}) {
  const suggestions = new Map();
  for (const [id, suggestion] of diarySuggestions(entries, matters, options)) {
    suggestions.set(id, suggestion.status
      ? { ...suggestion, status: VALS_STATUS_NAMES[suggestion.status] || suggestion.status }
      : suggestion);
  }
  return suggestions;
}

// The fields a VALS row takes from a suggestion, whether filled automatically or by "Use this".
export function valsFillFields(entry, suggestion, now = new Date()) {
  const inPlace = suggestion.status === "Aid in place";
  return {
    aidStatus: suggestion.status,
    legalAidInPlace: inPlace,
    aidNumber: inPlace ? suggestion.aidNumber || entry.aidNumber || "" : "",
    otherStatusText: "",
    budgetPaymentApprovedByML: false,
    aidStatusFromImport: false,
    aidPlannerFill: { matterId: suggestion.matterId, status: suggestion.status, reason: suggestion.reason || "", at: now.toISOString() },
  };
}

export function fillValsDiaryFromAidPlanner(entries = [], suggestions = new Map(), now = new Date()) {
  let changed = false;
  const next = entries.map((entry) => {
    const suggestion = suggestions.get(entry.id);
    if (!suggestion?.status || !entry.aidStatusFromImport || entry.solicitorConfirmed) return entry;
    changed = true;
    return { ...entry, ...valsFillFields(entry, suggestion, now) };
  });
  return changed ? next : entries;
}
